import React, {
  useEffect, useState, useMemo, useCallback
} from "react";
import { Helmet } from "react-helmet-async";

import { db } from "../firebase";

import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  documentId,
} from "firebase/firestore";
import "./Display/TFTGridDisplay.css";
import { useCart } from "../context/CartContext";
import { generalSpecsToRows } from "../context/CompareContext";
import CompareToggle from "../components/CompareToggle";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import { slugify } from "../slugify";
import { CartIcon, DownloadIcon } from "../components/ActionIcons";
import { IMAGE_COMING_SOON } from "../imageComingSoon";
import { productAltBase } from "../utils/imageAlt";


const ProductListing = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const {
    categorySlug,
    subCategorySlug,
  } = useParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  // The filter/sort sidebar becomes an off-canvas drawer on mobile
  // (see .tft-sidebar in TFTGridDisplay.css) -- without this trigger
  // there was no way to open it, so mobile visitors could never filter
  // or sort this grid at all.
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Lock background scroll while the mobile filter drawer is open.
  // Plain overflow:hidden is what NOT to use here -- on iOS Safari it can
  // make this full-screen position:fixed drawer (and its backdrop/trigger)
  // paint at the wrong offset instead of covering the viewport, which is
  // exactly "the filter button/drawer doesn't work" on a phone. Pinning
  // body with position:fixed and restoring scroll on close is the
  // standard iOS-safe scroll lock (see NavBar.jsx for the same pattern).
  useEffect(() => {
    if (isMobileFilterOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10) * -1);
      }
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
    };
  }, [isMobileFilterOpen]);

  const [categoryName, setCategoryName] = useState("");
  const [subCategoryDisplayName, setSubCategoryDisplayName] = useState("");
  // Field names (from the subcategory's "fields" schema collection) whose
  // admin-defined _AddinDescription flag is true — those fields' values
  // show as the card's description instead of a full spec dump.
  const [descriptionFieldNames, setDescriptionFieldNames] = useState(new Set());
  // Dynamic — keys are discovered from whatever field names actually exist
  // in the data (see dynamicFieldNames below), not a fixed hardcoded list.
  const [filters, setFilters] = useState({});
  // Sections with more than VISIBLE_OPTION_LIMIT options start collapsed
  // (show first N + a "+ N more" link) so long option lists never need a
  // scrollbar — expanding one is tracked per field name here.
  const [expandedSections, setExpandedSections] = useState({});
  const VISIBLE_OPTION_LIMIT = 6;

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // Tracks which card is hovered so its image can swap to the back shot.
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const rowsPerPage = 12;


  const formatValue = (val) => {
    if (val instanceof Date) {
      return val.getFullYear() < 1900 ? "" : val.toLocaleDateString("en-GB");
    }
    return String(val ?? "");
  };

  const isScalar = (val) =>
    val !== undefined &&
    val !== null &&
    typeof val !== "object";

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  
  const FIELD_PATTERNS = {
    title: [/^product.?name$/i, /^tit+le$/i, /^name$/i],
    displaySize: [/display.?size/i, /panel.?size/i, /^size$/i],
    resolution: [/resolution/i],
    partNo: [/part.?no/i, /part.?number/i, /model.?no/i],
    displayMode: [/display.?mode/i, /^mode$/i],
    brightness: [/brightness/i, /nits?/i],
    interfaceType: [/interface/i],
    connectorPins: [/connector/i, /^pins?$/i],
    controller: [/controller/i, /driver.?ic/i],
    touchPanel: [/touch/i],
    operatingTemp: [/operating.?temp/i],
    // Fields kept out of the filter sidebar (per product request) but
    // still shown in the card's spec summary line.
    activeArea: [/active.?area/i],
    outlineDimension: [/outline.?dimension/i],
    operatingVoltage: [/operating.?voltage/i],
    storageTemp: [/storage.?temp/i],
    price: [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i],
    stockPrice: [/stock.?price/i],
    stock: [/^stock$/i, /^qty$/i, /quantity/i, /stock.?(qty|quantity|count)/i, /availability/i],
    description: [/^description$/i, /^desc$/i, /^about$/i, /^summary$/i, /^details$/i],
    
    image: [/image/i, /photo/i, /picture/i],
    // Any field that's clearly a datasheet/drawing/spec PDF link — this
    // was showing up as a filter pill (one "option" per unique file URL),
    // which is meaningless as a filter.
    datasheetUrl: [/datasheet/i, /^dwg$/i, /drawing/i, /spec.?sheet/i, /\.pdf/i],
    video: [/^video/i],
  };

  const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

  // Some products have both a "title" and a "Title" field (a data-entry
  // artifact), and Firestore map-field key order isn't guaranteed stable
  // across reads, so picking the first pattern match arbitrarily flips
  // between the empty one and the real one on different loads. Prefer
  // whichever match actually has a value over an empty one.
  const matchField = (fieldsObj, patterns) => {
    const entries = Object.entries(fieldsObj || {}).filter(([key]) =>
      patterns.some((p) => p.test(key))
    );
    if (entries.length === 0) return "";
    const nonEmpty = entries.find(([, value]) => value !== "" && value != null);
    return (nonEmpty || entries[0])[1] ?? "";
  };

  // Real front/back product images — Tixaru's own Front Image Url / Back
  // Image Url fields only (synced verbatim by catalogSync.js). Any other
  // image-shaped field (gallery, a stray legacy field, etc.) is ignored
  // entirely rather than shown as a stand-in photo. "Image Coming Soon"
  // renders instead of a stock placeholder when neither is set.
  const getImagesFromFields = (fieldsObj) => {
    const frontEntry = Object.entries(fieldsObj || {}).find(([key]) => /front.*image/i.test(key));
    const backEntry = Object.entries(fieldsObj || {}).find(([key]) => /back.*image/i.test(key));

    const firstUrl = (raw) => String(raw || "").split(",")[0]?.trim();

    const front = (frontEntry && firstUrl(frontEntry[1])) || IMAGE_COMING_SOON;
    const back = (backEntry && firstUrl(backEntry[1])) || front;

    return { front, back };
  };

  useEffect(() => {

    if (!categorySlug || !subCategorySlug) return;

    let isCancelled = false;
    let unsubscribe = null;

    const header = [
      "Display Size",
      "Resolution",
      "Part No",
      "Display Mode",
      "Brightness",
      "Interface",
      "Connector Pins",
      "Controller",
      "Touch Panel",
      "Price",
      "Stock Price",
      "Stock",
    ];

    const setup = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setRows([]);

        // STEP 1 — find the category doc whose categoryName matches the URL
        const categorySnap = await getDocs(collection(db, "product_categories"));

        let categoryDoc = null;
        categorySnap.forEach((docSnap) => {
          if (slugify(docSnap.data().categoryName) === categorySlug) {
            categoryDoc = docSnap;
          }
        });

        if (!categoryDoc) {
          console.warn(`No category found for slug "${categorySlug}"`);
          if (!isCancelled) { setNotFound(true); setLoading(false); }
          return;
        }

        // STEP 2 — find the sub-category doc (within that category) whose
        // subCategoryName matches the URL
        const subCategorySnap = await getDocs(
          collection(db, "product_categories", categoryDoc.id, "subCategories")
        );

        let subCategoryDoc = null;
        subCategorySnap.forEach((docSnap) => {
          const name = docSnap.data().subCategoryName || docSnap.id;
          if (slugify(name) === subCategorySlug) {
            subCategoryDoc = docSnap;
          }
        });

        if (!subCategoryDoc) {
          console.warn(`No sub-category found for slug "${subCategorySlug}"`);
          if (!isCancelled) { setNotFound(true); setLoading(false); }
          return;
        }

        const subCategoryName = subCategoryDoc.data()?.subCategoryName || subCategoryDoc.id;
        if (!isCancelled) {
          setCategoryName(categoryDoc.data()?.categoryName || "");
          setSubCategoryDisplayName(subCategoryName);
        }

        // Field schema for this subcategory — defines which field names
        // should surface as the card's description (_AddinDescription:
        // true) instead of just sitting in the raw fields map unlabeled.
        const fieldsSchemaSnap = await getDocs(
          collection(
            db, "product_categories", categoryDoc.id,
            "subCategories", subCategoryDoc.id, "fields"
          )
        );
        const descFieldNames = new Set();
        fieldsSchemaSnap.forEach((d) => {
          const schema = d.data();
          if (schema._AddinDescription === true && schema.fieldName) {
            descFieldNames.add(String(schema.fieldName).trim().toLowerCase());
          }
        });
        if (!isCancelled) setDescriptionFieldNames(descFieldNames);

        // STEP 3 — LIVE-listen directly to this subcategory's productData
        // rows. No "products" grouping layer in between — that assumption
        // was wrong and caused every category/subcategory to show "No
        // displays match your criteria" (the old code queried a
        // collection that never had any documents in it).
        // Ordered explicitly by document ID — this must match the same
        // explicit order used in TFTDetailPage/FrequentlyBoughtTogether's
        // own productData fetches, or the duplicate-suffix slug computed
        // here (used to build each card's link) can drift out of sync with
        // what the detail page resolves for that same URL.
        const productDataRef = query(
          collection(
            db, "product_categories", categoryDoc.id,
            "subCategories", subCategoryDoc.id, "productData"
          ),
          orderBy(documentId())
        );

        unsubscribe = onSnapshot(
          productDataRef,
          (snap) => {
            const newRows = [];
            // Two products can share the same (or missing) Title within a
            // subcategory — the first occurrence keeps the clean slug, any
            // later duplicate gets its doc id appended so the URL stays
            // unique. TFTDetailPage replicates this exact same assignment
            // over the same Firestore-ordered snapshot to resolve it back.
            const usedTitleSlugs = new Set();

            snap.forEach((rowDoc) => {
              const f = rowDoc.data().fields || {};
              const title = matchField(f, FIELD_PATTERNS.title) || subCategoryName;

              let titleSlug = slugify(title);
              if (usedTitleSlugs.has(titleSlug)) {
                titleSlug = `${titleSlug}-${rowDoc.id.slice(0, 6).toLowerCase()}`;
              }
              usedTitleSlugs.add(titleSlug);

              newRows.push([
                matchField(f, FIELD_PATTERNS.displaySize),
                matchField(f, FIELD_PATTERNS.resolution),
                matchField(f, FIELD_PATTERNS.partNo),
                matchField(f, FIELD_PATTERNS.displayMode),
                matchField(f, FIELD_PATTERNS.brightness),
                matchField(f, FIELD_PATTERNS.interfaceType),
                matchField(f, FIELD_PATTERNS.connectorPins),
                matchField(f, FIELD_PATTERNS.controller),
                matchField(f, FIELD_PATTERNS.touchPanel),
                matchField(f, FIELD_PATTERNS.price),
                matchField(f, FIELD_PATTERNS.stockPrice),
                matchField(f, FIELD_PATTERNS.stock),
                // Human-readable routing slug — categorySlug/subCategorySlug
                // are already this page's own URL params, so only the
                // title slug needs carrying.
                titleSlug,
                title,
                // Raw fields map, appended last — used by the dynamic
                // filter sidebar AND by the front/back image lookup.
                f,
              ]);
            });

            if (!isCancelled) {
              setRows([header, ...newRows]);
              setLoading(false);
            }
          },
          (err) => {
            console.error(err);
            if (!isCancelled) setLoading(false);
          }
        );

      } catch (error) {
        console.error(error);
        if (!isCancelled) setLoading(false);
      }
    };

    setup();

    return () => {
      isCancelled = true;
      if (unsubscribe) unsubscribe();
    };

  }, [categorySlug, subCategorySlug]);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll(
      ".tft-header-content, .tft-image-collage, .tft-filter-bar, .tft-grid-wrapper"
    );
    const reveal = () => {
      els.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight - 60) {
          el.classList.add("reveal");
        }
      });
    };
    window.addEventListener("scroll", reveal);
    reveal();
    return () => window.removeEventListener("scroll", reveal);
  }, [rows]);

 
  const RESERVED_FILTER_PATTERNS = [
    ...FIELD_PATTERNS.title,
    ...FIELD_PATTERNS.partNo,
    ...FIELD_PATTERNS.price,
    ...FIELD_PATTERNS.stockPrice,
    ...FIELD_PATTERNS.stock,
    ...FIELD_PATTERNS.description,
    ...FIELD_PATTERNS.image,
    ...FIELD_PATTERNS.datasheetUrl,
    // Still shown in the card's spec summary — just not worth filtering by.
    ...FIELD_PATTERNS.activeArea,
    ...FIELD_PATTERNS.outlineDimension,
    ...FIELD_PATTERNS.operatingVoltage,
    ...FIELD_PATTERNS.storageTemp,
    ...FIELD_PATTERNS.video,
    /^isfeatured$/i,
      /^about.?product$/i,
  /^moq$/i,
  /^lead.?time$/i,
  // Free-text marketing paragraph (Items page -> Attributes tab), not a
  // spec value -- was showing up as a filter section listing entire
  // sentences as "options".
  /^general.?specs.?notes$/i,
  // Per product request -- not useful as filters, still shown in the
  // card's spec summary / Specifications tab.
  /^tft.?pin.?count$/i,
  /^outline.?area$/i,
  /^viewing.?direction$/i,
  /^top.?polarizer/i,
  /^touch.?pin.?count$/i,
  ];
  const isReservedFilterKey = (key) =>
    RESERVED_FILTER_PATTERNS.some((p) => p.test(key.trim()));

  const FILTER_ORDER = [
    FIELD_PATTERNS.displaySize,
    FIELD_PATTERNS.resolution,
    FIELD_PATTERNS.brightness,
    FIELD_PATTERNS.operatingTemp,
    FIELD_PATTERNS.interfaceType,
    FIELD_PATTERNS.touchPanel,
  ];
  const filterOrderRank = (key) => {
    const idx = FILTER_ORDER.findIndex((patterns) => testKey(key, patterns));
    return idx === -1 ? FILTER_ORDER.length : idx;
  };


  const dynamicFieldNames = useMemo(() => {
    if (rows.length <= 1) return [];
    const names = new Set();
    rows.slice(1).forEach((row) => {
      const rawFields = row[14] || {};
      Object.entries(rawFields).forEach(([key, val]) => {
        if (isReservedFilterKey(key)) return;
        if (!isScalar(val)) return;
        names.add(key);
      });
    });
    return Array.from(names).sort((a, b) => filterOrderRank(a) - filterOrderRank(b));
  }, [rows]);

  // Cascading options — for each dynamic field, only consider rows that
  // already satisfy every OTHER active filter, so picking a value in one
  // filter narrows what's selectable in the others.
  const availableOptions = useMemo(() => {
    if (rows.length <= 1) return {};

    const base = rows.slice(1);

    const optsFor = (fieldName) => {
      const set = new Set();
      base
        .filter((row) => {
          const rawFields = row[14] || {};
          return Object.entries(filters).every(([key, values]) => {
            if (key === fieldName) return true;
            if (!values || values.length === 0) return true;
            return values.includes(rawFields[key]);
          });
        })
        .forEach((row) => {
          const rawFields = row[14] || {};
          const val = rawFields[fieldName];
          // Only ever add scalar values as filter options — a stray
          // object/array value for this field on some other row must
          // never reach the option set (and therefore never reach JSX).
          if (val !== "" && isScalar(val)) set.add(val);
        });
      return Array.from(set)
        .filter((x) => String(x).toLowerCase() !== "all")
        .sort((a, b) => parseFloat(a) - parseFloat(b) || String(a).localeCompare(String(b)));
    };

    const result = {};
    dynamicFieldNames.forEach((name) => {
      result[name] = optsFor(name);
    });
    return result;
  }, [rows, filters, dynamicFieldNames]);

  const handleFilterChange = useCallback((fieldName, value) => {
    setFilters((prev) => {
      const currentValues = prev[fieldName] || [];

      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [fieldName]: updatedValues,
      };
    });

    setCurrentPage(1);
  }, []);

  const clearFilters = () => {
    setFilters({});
    setSearch("");
    setInStockOnly(false);
    setCurrentPage(1);
  };

  const filteredRows = useMemo(() => {
    return rows.slice(1).filter((row) => {
      const rawFields = row[14] || {};

      // row[11] is the Stock column; blank or non-numeric counts as out of stock.
      if (inStockOnly && !(parseFloat(row[11]) > 0)) return false;

      const matchesFilters = Object.entries(filters).every(([key, values]) => {
        if (!values || values.length === 0) return true;
        return values.includes(rawFields[key]);
      });
      if (!matchesFilters) return false;

      if (!search.trim()) return true;
      const searchLower = search.toLowerCase();

      const matchesFixedColumns = row
        .slice(0, 11)
        .some((c) => formatValue(c).toLowerCase().includes(searchLower));
      const matchesDynamicFields = Object.values(rawFields).some((v) =>
        isScalar(v) && String(v ?? "").toLowerCase().includes(searchLower)
      );

      return matchesFixedColumns || matchesDynamicFields;
    });
  }, [rows, filters, search, inStockOnly]);


  const priceOf = (row) => {
    const p = parseFloat(row[9]);
    return isNaN(p) ? null : p;
  };

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];

    switch (sortBy) {
      case "price-asc":
        sorted.sort((a, b) => {
          const pa = priceOf(a), pb = priceOf(b);
          if (pa === null && pb === null) return 0;
          if (pa === null) return 1;
          if (pb === null) return -1;
          return pa - pb;
        });
        break;

      case "price-desc":
        sorted.sort((a, b) => {
          const pa = priceOf(a), pb = priceOf(b);
          if (pa === null && pb === null) return 0;
          if (pa === null) return 1;
          if (pb === null) return -1;
          return pb - pa;
        });
        break;

      case "name-asc":
        sorted.sort((a, b) => String(a[13] || "").localeCompare(String(b[13] || "")));
        break;

      case "name-desc":
        sorted.sort((a, b) => String(b[13] || "").localeCompare(String(a[13] || "")));
        break;

      default:
        break;
    }

    return sorted;
  }, [filteredRows, sortBy]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

  const currentRows = sortedRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const isMobile = windowWidth <= 768;
  const maxBtns = isMobile ? 5 : Math.min(totalPages, 14);
  let startPage = Math.max(1, Math.min(currentPage - Math.floor(maxBtns / 2), totalPages - maxBtns + 1));
  let endPage = Math.min(totalPages, startPage + maxBtns - 1);
  const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const hasFilters =
    Object.values(filters).some(
      (arr) => arr && arr.length > 0
    ) || search || inStockOnly;

  if (notFound) {
    return (
      <div className="tft-layout">
        <Helmet>
          <title>Category Not Found | BIBA Electronics</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <main className="tft-page">
          <div className="loading-state">
            <p>We couldn't find this category/sub-category.</p>
          </div>
        </main>
      </div>
    );
  }

  const listingTitle = subCategoryDisplayName || categoryName;
  const metaTitle = listingTitle
    ? `${listingTitle}${/displays?$/i.test(listingTitle) ? "" : " Displays"} | BIBA Electronics`
    : "Displays | BIBA Electronics";
  const SITE_URL = "https://www.bibaelectronics.com";
  const listingUrl = `${SITE_URL}/category/${categorySlug}/${subCategorySlug}`;
  const listingJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: listingTitle || "Displays", item: listingUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: metaTitle.replace(/ \| BIBA Electronics$/, ""),
      url: listingUrl,
      // rows[0] is the header row; the last three cells of a product row are
      // titleSlug, title and the raw fields map (see the rows.push above).
      itemListElement: rows.slice(1, 51).map((row, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: row[row.length - 2],
        url: `${SITE_URL}/tft/${categorySlug}/${subCategorySlug}/${row[row.length - 3]}`,
      })),
    },
  ];
  const metaDescription = listingTitle
    ? `${listingTitle} from BIBA Electronics, Gurgaon, India. Compare size, resolution, interface and touch options with specs, datasheets and pricing.`
    : "Browse TFT displays and HMI panels from BIBA Electronics — specs, pricing and datasheets.";

  return (
    <div className="tft-layout">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {listingJsonLd.map((block, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
        ))}
      </Helmet>

      <main className="tft-page">

        <h1 className="tft-page-title">{listingTitle || "Displays"}</h1>

        {/* ── GRID SECTION ── */}
        {rows.length > 0 ? (
          <div className="tft-content-layout">

            <button
              className="mobile-filter-trigger"
              onClick={() => setIsMobileFilterOpen(true)}
              aria-label="Open filters"
            >
              Filter &amp; Sort
            </button>

            <div
              className={`tft-sidebar-overlay ${isMobileFilterOpen ? "active" : ""}`}
              onClick={() => setIsMobileFilterOpen(false)}
            />

            <aside className={`tft-sidebar ${isMobileFilterOpen ? "mobile-open" : ""}`}>

              <div className="sidebar-mobile-header">
                <h3>Filters</h3>
                <button
                  className="sidebar-close-btn"
                  onClick={() => setIsMobileFilterOpen(false)}
                  aria-label="Close filters"
                >
                  ✕
                </button>
              </div>

              <div className="sidebar-section">

                <select
                  className="top-sort-dropdown sidebar-sort"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Sort By</option>
                  <option value="price-asc">Price: Low → High</option>
                  <option value="price-desc">Price: High → Low</option>
                  <option value="name-asc">Name: A → Z</option>
                  <option value="name-desc">Name: Z → A</option>
                </select>

              </div>

              {/* FILTERS — fully dynamic. A field only gets a section if
                  it actually has at least one available value right now;
                  empty sections (no matching data) are skipped entirely
                  rather than showing an empty heading. */}
              {dynamicFieldNames.map((fieldName) => {
                const opts = availableOptions[fieldName] || [];
                if (opts.length === 0) return null;

                const isExpanded = !!expandedSections[fieldName];
                const visibleOpts = isExpanded
                  ? opts
                  : opts.slice(0, VISIBLE_OPTION_LIMIT);
                const hiddenCount = opts.length - VISIBLE_OPTION_LIMIT;

                return (
                <div className="sidebar-section" key={fieldName}>

                  <h3 className="sidebar-title">{fieldName}</h3>

                  <div className="sidebar-checkbox-group">
                    {visibleOpts.map((opt, i) => {
                      const active = (filters[fieldName] || []).includes(opt);
                      return (
                        <label className="sidebar-checkbox-row" key={i}>
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() =>
                              handleFilterChange(fieldName, opt)
                            }
                          />
                          <span className="checkbox-custom" />
                          {/* opts is already scalar-only (see optsFor
                              above), so String(opt) is always safe and
                              never a raw object/array. */}
                          <span className="checkbox-label">{String(opt)}</span>
                        </label>
                      );
                    })}
                  </div>

                  {opts.length > VISIBLE_OPTION_LIMIT && (
                    <button
                      type="button"
                      className="sidebar-show-more"
                      onClick={() =>
                        setExpandedSections((prev) => ({
                          ...prev,
                          [fieldName]: !prev[fieldName],
                        }))
                      }
                    >
                      {isExpanded ? "Show less" : `+ ${hiddenCount} more`}
                    </button>
                  )}
                </div>
                );
              })}

              {/* CLEAR */}
              {hasFilters && (
                <button className="sidebar-clear-btn" onClick={clearFilters}>
                  Clear All Filters
                </button>
              )}

            </aside>
            <div className="tft-grid-wrapper">

              <div className="tft-search-row">

                <div className="tft-search-container">
                  <svg className="tft-search-icon" viewBox="0 0 20 20" fill="none">
                    <circle cx="8.5" cy="8.5" r="5.5" stroke="#9CA3AF" strokeWidth="1.5" />
                    <path
                      d="M13 13l3 3"
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>

                  <input
                    type="text"
                    placeholder="Search displays..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <button
                  type="button"
                  className={`tft-instock-btn${inStockOnly ? " active" : ""}`}
                  aria-pressed={inStockOnly}
                  onClick={() => {
                    setInStockOnly((v) => !v);
                    setCurrentPage(1);
                  }}
                >
                  Show only in-stock items
                </button>

              </div>

              {hasFilters && (
                <div className="active-filters-bar">
                  {Object.entries(filters).flatMap(([key, values]) =>
                    (values || []).map((value) => (
                      <button
                        key={`${key}-${value}`}
                        className="active-filter-chip"
                        onClick={() =>
                          handleFilterChange(key, value)
                        }
                      >
                        <strong>{key}</strong>
                        {/* value here always originated from optsFor's
                            scalar-only set, so String(value) is safe. */}
                        : {String(value)}
                        <span>✕</span>
                      </button>
                    ))
                  )}
                  <button
                    className="clear-all-chip"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>

                </div>
              )}

              <div className="grid-topbar" />

              {currentRows.length > 0 ? (
                <div className="tft-grid">

                  {currentRows.map((row, i) => {

                    const [
                      displaySize,
                      resolution,
                      partNo,
                      displayMode,
                      brightness,
                      iface,
                      connPins,
                      controller,
                      touchPanel,
                      price,
                      stockPrice,
                      stock,
                      rowPartNoSlug,
                      title,
                      rawFields,
                    ] = row;

                    const priceNum = parseFloat(price);
                    const hasPrice = price !== "" && !isNaN(priceNum);

                    const stockPriceNum = parseFloat(stockPrice);
                    const hasStockPrice = stockPrice !== "" && !isNaN(stockPriceNum);

                    const { front, back } = getImagesFromFields(rawFields);
                    const isHovered = hoveredRowKey === i;
                    const route = `/tft/${categorySlug}/${subCategorySlug}/${rowPartNoSlug}`;
                    // Keyed by route/slug rather than Part No -- some rows
                    // have no Part No filled in on Tixaru, and several blank
                    // ones would otherwise collide into a single compare slot.
                    const compareProduct = {
                      id: route,
                      partNo,
                      title,
                      image: front,
                      price: hasPrice ? priceNum : null,
                      route,
                      specs: generalSpecsToRows(rawFields.generalSpecs),
                    };

                    return (
                      <div
                        className="tft-card"
                        key={i}
                        onClick={() =>
                          // Human-readable URL built from real part number.
                          // categorySlug/subCategorySlug are this page's
                          // own URL params already — no product-line
                          // segment anymore, since that grouping layer
                          // doesn't exist in the real data.
                          navigate(route)
                        }
                        onMouseEnter={() => setHoveredRowKey(i)}
                        onMouseLeave={() => setHoveredRowKey(null)}
                      >
                        <div className="tft-card-top">

                          <div className="tft-part-number">
                            {partNo}
                          </div>

                          <CompareToggle product={compareProduct} />

                        </div>

                        <div className="tft-card-image">
                          <img
                            src={isHovered ? back : front}
                            loading="lazy"
                            decoding="async"
                            alt={`${productAltBase({ partNo, typeName: subCategoryDisplayName || categoryName, fields: rawFields })} ${isHovered && back ? "back" : "front"} view`}
                          />
                        </div>

                        <div className="tft-card-content">

                          <div className="tft-title-row">
                            <h3
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                margin: 0,
                              }}
                            >
                              {title}
                            </h3>
                          </div>

                          {(() => {
              
                            const descriptionParts = Object.entries(rawFields)
  .filter(
    ([key, value]) =>
      descriptionFieldNames.has(key.trim().toLowerCase()) &&
      value !== "" &&
      value != null
  );

const fallbackDescription = matchField(rawFields, FIELD_PATTERNS.description);

if (descriptionParts.length > 0) {
  return (
    <div className="tft-spec-list">
      {descriptionParts.slice(0, 3).map(([label, value]) => (
    <div className="spec-row" key={label}>
        <span className="spec-label">{label}</span>
        <span className="spec-value">{String(value)}</span>
    </div>
))}
    </div>
  );
}

return fallbackDescription ? (
  <div className="tft-spec-summary">
    {fallbackDescription}
  </div>
) : null;
                          })()}

                          <div className="tft-card-info">
                            <div
                              className="tft-price"
                              style={{ fontSize: "1.15rem", fontWeight: 800, color: "#15803d" }}
                            >
                              {hasPrice ? `Price: ₹${priceNum.toLocaleString()}` : "Price: -"}
                            </div>
                          </div>

                          <div className="tft-card-footer">

                            <button
                              className="add-cart-btn"
                              disabled={!hasPrice}
                              title={hasPrice ? undefined : "Price coming soon"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!hasPrice) return; // guards a direct call too, not just the disabled state
                                addToCart({
                                  partNo, displaySize, resolution, brightness, iface, touchPanel,
                                  price: priceNum,
                                  quantity: 1,
                                });
                              }}
                            >
                              <CartIcon size={16} className="add-cart-btn-icon" />
                              {hasPrice ? "Add To Cart" : "Price coming soon"}
                            </button>


                            {partNo ? (
                              <a
                                href={`/assets/Biba Data Sheet/DWG ${partNo}.pdf`}
                                download={`DWG ${partNo}.pdf`}
                                className="datasheet-icon-btn footer-download-btn"
                                title="Download Datasheet"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DownloadIcon size={18} />
                              </a>
                            ) : (
                              <button
                                className="datasheet-icon-btn footer-download-btn disabled"
                                disabled
                              >
                                <DownloadIcon size={18} />
                              </button>
                            )}

                          </div>
                        </div>

                      </div>
                    );
                  })}

                </div>
              ) : (
                <div className="no-results-grid">
                  No displays match your criteria
                </div>
              )}

              {totalPages > 1 && (
                <div className="pagination">

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.max(p - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="pg-arrow"
                  >
                    ◀
                  </button>

                  {startPage > 1 && (
                    <>
                      <button onClick={() => setCurrentPage(1)}>
                        1
                      </button>

                      {startPage > 2 && (
                        <span className="pg-ellipsis">…</span>
                      )}
                    </>
                  )}

                  {visiblePages.map((n) => (
                    <button
                      key={n}
                      className={currentPage === n ? "active" : ""}
                      onClick={() => setCurrentPage(n)}
                    >
                      {n}
                    </button>
                  ))}

                  {endPage < totalPages && (
                    <>
                      {endPage < totalPages - 1 && (
                        <span className="pg-ellipsis">…</span>
                      )}

                      <button
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(p + 1, totalPages)
                      )
                    }
                    disabled={currentPage === totalPages}
                    className="pg-arrow"
                  >
                    ▶
                  </button>

                </div>
              )}

            </div>

          </div>
        ) : (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading display data…</p>
          </div>
        )}

      </main>
    </div>
  );
};

export default ProductListing;